import {
  CountryCode,
  getCountryCallingCode,
  getCountries,
} from "libphonenumber-js";

export interface NormalizedPhone {
  original: string;
  cleaned: string; // Number with country code, e.g. "+27766791145"
  country: string; // ISO country code, e.g. "ZA"
}

function getCountryCodeFromCallingCode(
  callingCode: string
): CountryCode | undefined {
  const countries = getCountries();
  return countries.find(
    (country) => getCountryCallingCode(country) === callingCode
  );
}

export function normalizePhoneNumber(
  rawNumber: string,
  defaultCallingCode: string = "+27"
): NormalizedPhone {
  if (!rawNumber) {
    return {
      original: "",
      cleaned: "",
      country: getCountryCodeFromCallingCode(defaultCallingCode) || "ZA",
    };
  }

  const country = getCountryCodeFromCallingCode(defaultCallingCode) || "ZA";

  // Trim whitespace
  rawNumber = rawNumber.trim();

  // Case 1: Already starts with + and digits -> return as-is
  if (/^\+\d+$/.test(rawNumber)) {
    return {
      original: rawNumber,
      cleaned: rawNumber,
      country,
    };
  }

  // Remove all non-digit characters
  let digits = rawNumber.replace(/\D/g, "");

  // Case 2: Starts with 0 -> replace with country code
  if (digits.startsWith("0")) {
    digits = digits.replace(/^0+/, "");
    return {
      original: rawNumber,
      cleaned: `${defaultCallingCode}${digits}`,
      country,
    };
  }

  // Case 3: Starts with country code
  if (digits.startsWith(defaultCallingCode)) {
    return {
      original: rawNumber,
      cleaned: `+${digits}`,
      country,
    };
  }

  // Default: prepend country code
  return {
    original: rawNumber,
    cleaned: `${defaultCallingCode}${digits}`,
    country,
  };
}

export function stripCountryCode(
  phoneNumber: string,
  defaultCallingCode: string = "27"
): string {
  const digits = phoneNumber.replace(/\D/g, "");
  return digits.startsWith(defaultCallingCode)
    ? digits.slice(defaultCallingCode.length)
    : digits;
}
